import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import { useSnackbar } from 'notistack';
import { STORAGE_KEY_ENUM } from 'enums';
import apiService from 'library/api-service';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { renderDataGridLocaleText } from 'utils';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Grid2 as Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import usedBanksActions from 'store/features/used-banks/actions';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { CreateUsedBankDialog, UpdateUsedBankDialog } from './components';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { selectUsedBanks, selectUsedBanksIsEmpty, selectUsedBanksLoading } from 'store/features/used-banks/selector';

const UsedBanks = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const dispatch = useAppDispatch();
  const usedBanks = useAppSelector(selectUsedBanks);
  const loading = useAppSelector(selectUsedBanksLoading);
  const permissionCodes = useAppSelector(selectPermissionCodes);
  const isEmptyUsedBanks = useAppSelector(selectUsedBanksIsEmpty);

  // useState hooks
  const [openDeleteUsedBank, setOpenDeleteUsedBank] = useState(false);
  const [openCreateUsedBank, setOpenCreateUsedBank] = useState(false);
  const [openUpdateUsedBank, setOpenUpdateUsedBank] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'usedBanks.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const refresh = async () => {
    dispatch(usedBanksActions.getUsedBanks());
  };

  // sync helper functions
  const handleCloseDeleteUsedBank = () => setOpenDeleteUsedBank(false);
  const handleClickOpenDeleteUsedBank = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenDeleteUsedBank(true);
  };

  const handleCloseCreateUsedBank = () => setOpenCreateUsedBank(false);
  const handleClickOpenCreateUsedBank = () => setOpenCreateUsedBank(true);

  const handleCloseUpdateUsedBank = () => setOpenUpdateUsedBank(false);
  const handleClickOpenUpdateUsedBank = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenUpdateUsedBank(true);
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-bank')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    if (!usedBanks.length && !isEmptyUsedBanks) {
      dispatch(usedBanksActions.getUsedBanks());
    }
  }, [usedBanks, isEmptyUsedBanks, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['create-bank']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenCreateUsedBank}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage id="usedBanks.createUsedBank" defaultMessage="Banka Oluştur" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-bank']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenUpdateUsedBank}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<EditIcon />}
              color="secondary">
              <FormattedMessage id="usedBanks.updateUsedBank" defaultMessage="Banka Düzenle" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-bank']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenDeleteUsedBank}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage id="usedBanks.deleteUsedBank" defaultMessage="Banka Sil" />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateUsedBankDialog open={openCreateUsedBank} handleClose={handleCloseCreateUsedBank} refresh={refresh} />
      <UpdateUsedBankDialog
        open={openUpdateUsedBank}
        usedBankId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateUsedBank}
        refresh={refresh}
      />
      <DeleteDialog
        open={openDeleteUsedBank}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteUsedBank}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="usedBanks.deleteUsedBank" defaultMessage="Banka Sil" />}
        subTitle={
          <FormattedMessage
            id="usedBanks.deleteUsedBankRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra banka veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteUsedBank}
        successMessage={
          <FormattedMessage
            id="usedBanks.deleteUsedBankCompleted"
            defaultMessage="Seçili bankayı silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={usedBanks}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default UsedBanks;
